import { useState } from "react";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IRelatedProduct } from "src/api/client/related-products.api.types";
import { IconCaret } from "src/components/icons-mui/caret";
import { RelatedWikifolioCard } from "src/components/pages/detail/related-wikifolios/related-wikifolio-card";
import { Button } from "src/components-mui/base/button/button";
import { Typography } from "src/components-mui/base/typography/typography";

const INITIAL_VISIBLE_COUNT = 2;

export interface IExpandableRelatedWikifoliosListProps {
    title: string;
    products: IRelatedProduct[];
    imageUrl: string;
}

export const ExpandableRelatedWikifoliosList = ({ title, products, imageUrl }: IExpandableRelatedWikifoliosListProps) => {
    const { t } = useTranslation("common");
    const [isExpanded, setIsExpanded] = useState(false);

    const shouldShowExpandButton = products.length > INITIAL_VISIBLE_COUNT;
    const visibleProducts = isExpanded ? products : products.slice(0, INITIAL_VISIBLE_COUNT);

    return (
        <Box>
            <Typography
                component="h3"
                sx={{
                    fontSize: "md",
                    fontWeight: "bold",
                    mb: 1,
                }}
            >
                {title}
            </Typography>
            <Stack
                sx={{
                    gap: 1,
                }}
            >
                {visibleProducts.map(product => (
                    <RelatedWikifolioCard
                        key={product.wikifolioId}
                        title={product.shortDescription}
                        label={product.label ?? ""}
                        href={product.detailUrl}
                        imageUrl={imageUrl}
                    />
                ))}
            </Stack>
            <Box
                sx={{
                    mt: 1,
                    textAlign: "right",
                }}
            >
                {shouldShowExpandButton && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setIsExpanded(prev => !prev)}
                        endIcon={
                            <IconCaret
                                sx={{
                                    width: "24px",
                                    height: "24px",
                                    transform: isExpanded ? "rotate(180deg)" : undefined,
                                }}
                            />
                        }
                        sx={{ pr: 0 }}
                    >
                        {isExpanded ? t("show-less") : t("show-more")}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
